/**
 * Client-side PDF text extraction for *standard* PDF attachments.
 *
 * Unlike the brochure pipeline (which rasterizes pages to images for a
 * server-side vision pass to build a site from), a standard PDF attachment just
 * needs its textual content in the conversation. We pull the text in-browser via
 * pdf.js and inject it into the message, so the text-first build model reads it
 * directly — no upload, no server changes.
 */

export const PDF_TEXT_LIMITS = {
  maxBytes: 25 * 1024 * 1024,
  // The only bound on extracted text. A brochure build needs EVERY page's
  // content (so no section is dropped), so there is no character cap — the page
  // count is the safety bound (same limit the renderer uses).
  maxPages: 40,
} as const;

export interface PdfTextResult {
  text: string;
  pageCount: number;
  truncated: boolean;
}

/** Composer-chip state for a (single) attached PDF read as text. */
export interface PdfDocAttachment {
  file: File;
  status: 'processing' | 'ready' | 'error';
  result?: PdfTextResult;
  error?: string;
}

/**
 * A file is a PDF if its MIME type says so OR — when the browser leaves the type
 * empty (some OS/file combos do) — its name ends in .pdf. Routing (ChatBox) and
 * the attach handlers (Chat.client) MUST use this same check, or a `.pdf` with an
 * empty MIME can pass routing yet be rejected on attach.
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export async function extractPdfText(file: File): Promise<PdfTextResult> {
  if (file.size > PDF_TEXT_LIMITS.maxBytes) {
    throw new Error(
      `PDF is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is ${PDF_TEXT_LIMITS.maxBytes / 1024 / 1024}MB`,
    );
  }

  // Dynamic import keeps pdf.js (~400KB + worker) out of the main bundle.
  const pdfjs = await import('pdfjs-dist');
  const { default: workerUrl } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const loadingTask = pdfjs.getDocument({
    data: await file.arrayBuffer(),
    // Assets copied into public/_pdfjs by scripts/copy-pdfjs-assets.mjs.
    wasmUrl: '/_pdfjs/wasm/',
    cMapUrl: '/_pdfjs/cmaps/',
    cMapPacked: true,
    standardFontDataUrl: '/_pdfjs/standard_fonts/',
  });

  let pdf;

  try {
    pdf = await loadingTask.promise;
  } catch (err: any) {
    if (err?.name === 'PasswordException') {
      throw new Error('This PDF is password-protected');
    }

    throw new Error(`Couldn't read this PDF: ${err?.message ?? err}`);
  }

  try {
    const pageCount = pdf.numPages;
    const pagesToRead = Math.min(pageCount, PDF_TEXT_LIMITS.maxPages);
    const parts: string[] = [];
    // Only truncated when the PDF genuinely exceeds the page bound.
    const truncated = pageCount > PDF_TEXT_LIMITS.maxPages;

    for (let pageNumber = 1; pageNumber <= pagesToRead; pageNumber++) {
      const page = await pdf.getPage(pageNumber);

      try {
        const content = await page.getTextContent();
        const text = content.items
          .map((item: any) => ('str' in item ? item.str : ''))
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();

        if (text) {
          // Page-label so the build model can map pages → sections and never
          // merge distinct topics. No char cap — consume EVERY page so a
          // multi-section brochure keeps all its content.
          parts.push(`--- Page ${pageNumber} ---\n${text}`);
        }
      } finally {
        page.cleanup();
      }
    }

    const out = parts.join('\n\n').trim();

    if (!out) {
      throw new Error('No readable text found in this PDF (it may be a scanned image)');
    }

    return { text: out, pageCount, truncated };
  } finally {
    pdf.cleanup?.();
  }
}
